import React, { useEffect } from 'react';
import { Tabs, Card, Text, Button, Flex } from '@aws-amplify/ui-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from "remark-gfm";
import '@aws-amplify/ui-react/styles.css';
import mermaid from 'mermaid';

function WebPageMain({ selectedItem, onRequestMeetingSummary, isSummaryGenerating }) {
  useEffect(() => {
    mermaid.initialize({ startOnLoad: true });
  }, []);

  const getCleanFileName = (objectName) => {
    if (!objectName) return '';
    // Extract filename from S3 path (e.g., "2025/Aug/2025-08-20/filename.mp3" -> "filename.mp3")
    const parts = objectName.split('/');
    const filename = parts[parts.length - 1];
    // Remove file extension for cleaner display
    return filename.replace(/\.[^/.]+$/, '');
  };

  const cardStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: 'none',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0'
  };

  const tabsContainerStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent overflow from affecting parent
  };

  const tabPanelStyle = {
    flex: 1,
    overflow: 'auto',
    padding: '2rem',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%'
  };

  const requestMeetingSummary = () => {
    if (selectedItem && selectedItem.object_name) {
      console.log('Requesting meeting summary for:', selectedItem.object_name);
      onRequestMeetingSummary(selectedItem).catch(error => {
        console.error('Error requesting meeting summary:', error);
      });
    } else {
      console.error('Cannot request meeting summary: selectedItem or key is missing');
    }
  };

  const downloadCombinedMarkdown = () => {
    const cleanName = getCleanFileName(selectedItem.object_name);
    const meetingSummary = selectedItem.meeting_summary || 'No meeting summary available.';
    const transcription = selectedItem.transcription_text || 'No transcription available.';
    
    const combinedContent = `# Meeting Notes: ${cleanName}

## Meeting Summary

${meetingSummary}

---

## Raw Transcription

${transcription}`;
    
    const filename = `${cleanName} - Complete Meeting Notes.md`;
    const element = document.createElement('a');
    const file = new Blob([combinedContent], {type: 'text/markdown'});
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!selectedItem) {
    return (
      <Card style={cardStyle}>
        <Flex direction="column" alignItems="center" justifyContent="center" height="100%" gap="1rem">
          <Text fontSize="4rem">📝</Text>
          <Text fontSize="1.3rem" fontWeight="600" color="gray">Select a meeting to view</Text>
          <Text fontSize="0.9rem" color="gray" textAlign="center" maxWidth="400px">
            Choose a meeting from the sidebar to see its summary and transcript, or upload a new audio file to get started
          </Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Card style={cardStyle}>
      <Flex direction="column" padding="1rem" borderBottom="1px solid #e0e0e0" gap="0.5rem">
        <Flex alignItems="center" gap="0.3rem" marginBottom="0.3rem">
          <Text fontSize="0.75rem" color="gray">Meetings</Text>
          <Text fontSize="0.75rem" color="gray">/</Text>
          <Text fontSize="0.75rem" fontWeight="600">{getCleanFileName(selectedItem.object_name)}</Text>
        </Flex>
        <Flex alignItems="center" justifyContent="space-between">
          <div>
            <Text fontSize="1.3rem" fontWeight="600">
              {getCleanFileName(selectedItem.object_name)}
            </Text>
            <Text fontSize="0.8rem" color="gray" marginTop="0.25rem">
              {formatDateTime(selectedItem.last_updated)}
            </Text>
          </div>
          {selectedItem.meeting_summary && (
            <Button size="small" onClick={downloadCombinedMarkdown}>
              Download
            </Button>
          )}
        </Flex>
      </Flex>
      
      <Tabs.Container defaultValue="meeting-notes" style={tabsContainerStyle}>
        <Tabs.List>
          <Tabs.Item value="meeting-notes">Meeting Summary</Tabs.Item>
          <Tabs.Item value="transcription">Full Transcript</Tabs.Item>
        </Tabs.List>
        <Tabs.Panel value="meeting-notes" style={tabPanelStyle}>
          {isSummaryGenerating ? (
            <Flex direction="column" alignItems="center" gap="1rem" padding="3rem">
              <Text fontSize="2rem">⏳</Text>
              <Text fontSize="1.2rem" fontWeight="600">Generating meeting summary...</Text>
              <Text color="gray">This may take a few minutes</Text>
            </Flex>
          ) : selectedItem.meeting_summary ? (
            <div className="meeting-content">
              <ReactMarkdown
                children={selectedItem.meeting_summary}
                remarkPlugins={[remarkGfm]}
              />
            </div>
          ) : (
            <Flex direction="column" alignItems="center" gap="1rem" padding="3rem">
              <Text fontSize="2rem">📄</Text>
              <Text fontSize="1.2rem" fontWeight="600">No meeting summary available</Text>
              <Text color="gray" textAlign="center" maxWidth="400px">
                Generate an AI-powered summary with key points, action items, and insights
              </Text>
              <Button onClick={requestMeetingSummary} disabled={isSummaryGenerating} variation="primary">
                {isSummaryGenerating ? 'Generating...' : 'Generate AI Summary'}
              </Button>
            </Flex>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="transcription" style={tabPanelStyle}>
          {selectedItem.transcription_text ? (
            <div className="meeting-content" style={{ fontSize: '0.95rem', color: '#444' }}>
              <ReactMarkdown
                children={selectedItem.transcription_text}
                remarkPlugins={[remarkGfm]}
              />
            </div>
          ) : (
            <Flex direction="column" alignItems="center" gap="1rem" padding="3rem">
              <Text fontSize="2rem">🎙️</Text>
              <Text fontSize="1.2rem" fontWeight="600">No transcription available yet</Text>
              <Text color="gray" textAlign="center" maxWidth="400px">
                Your audio file is being transcribed. This usually takes a few minutes.
              </Text>
            </Flex>
          )}
        </Tabs.Panel>
      </Tabs.Container>
    </Card>
  );
}

export default WebPageMain;
